/*
 * Reine Zeilen-, Gruppen- und Filterlogik der Datenpunkt-Waehler.
 *
 * Warum dieses Modul existiert: dieselbe Abflachung eines Anlagenschemas und
 * derselbe gerankte Filter standen bisher zweimal im Repo (charts/
 * MultiLeafPicker.tsx und, in einer aermeren Fassung, LeafPicker.tsx). Der
 * HMI-Editor braucht eine dritte Auswahl, und eine dritte Kopie waere die
 * Stelle, an der die drei auseinanderlaufen: der Chart-Waehler faende einen
 * Punkt, den der HMI-Waehler nicht findet, und niemand kaeme auf die Idee,
 * den Fehler im Waehler zu suchen.
 *
 * Das Modul ist bewusst frei von Laufzeit-Importen (nur Typ-Importe), damit
 * es ohne Bundler direkt testbar bleibt, genau wie `hmi/bindingKey.ts`.
 * Siehe `tests/datapointRows.test.ts`. Alles, was Hooks oder JSX braucht,
 * liegt daneben (`useVirtualRows.ts`, `DatapointPicker.tsx`).
 *
 * Was das Schema hergibt (mit Beleg, weil Raten hier teuer waere):
 *   - Werttyp: `LeafDto.valueType` ist der ToString() des Server-Enums
 *     `ValueTypeKind` (Bool, Int, Float, String, Enum, Flags), gesetzt in
 *     `src/features/Plants/HVACloud.Features.Plants/GetSchema/
 *     GetSchemaHandler.cs:183`, Enum in `src/shared/HVACloud.Model/Enums.cs:8`.
 *     Es ist also kein Freitext des Treibers.
 *   - Schreibbarkeit: `LeafDto.writability` ist der ToString() von
 *     `Writability` (ReadOnly, WriteOnly, ReadWrite), `Enums.cs:22`, gesetzt
 *     in `GetSchemaHandler.cs:193`. Dieselbe Pruefung wie in
 *     `components/SendCommandModal.tsx:41`.
 */

import type { PlantSchemaResponse, DeviceDto, NodeDto, LeafDto } from '../api/types';

/** Werttyp-Klassen, wie der Editor sie kennt. Groeber als das Server-Enum:
 *  ein Widget interessiert sich fuer "Zahl", nicht fuer Int gegen Float. */
export type DatapointValueType = 'number' | 'bool' | 'enum' | 'string';

/** Einschraenkung der waehlbaren Punkte, vom Aufrufer gesetzt. */
export interface DatapointFilter {
  writable?: boolean;
  valueType?: DatapointValueType;
}

/** Eine Datenpunkt-Zeile mit allem, was zum Unterscheiden zweier aehnlicher
 *  Punkte noetig ist. */
export interface DatapointRow {
  leaf: LeafDto;
  deviceId: string;
  deviceSymbol: string;
  /** Klartextname des Geraets, fuer die Geraete-Umschalter. Absichtlich NICHT
   *  im Heuhaufen: der muss zeichengleich zu dem bleiben, den MultiLeafPicker
   *  bisher gebaut hat, sonst aendert sich dort stillschweigend das
   *  Filterverhalten. */
  deviceName: string;
  /** Knotenpfad unterhalb des Geraets, etwa `Heizkreis 1/Vorlauf`. */
  path: string;
  /** Vorberechneter kleingeschriebener Suchtext. Eine Allokation je Zeile
   *  statt einer je Tastendruck und Zeile. */
  haystack: string;
}

/** Ein Geraet oder Knoten im Stoeberbaum. Gruppen ohne Treffer im Teilbaum
 *  kommen gar nicht erst vor, siehe {@link buildDatapointGroups}. */
export interface DatapointGroup {
  id: string;
  kind: 'device' | 'node';
  label: string;
  /** Pfad bis einschliesslich dieser Gruppe, ohne Geraet. Leer beim Geraet. */
  path: string;
  deviceId: string;
  deviceSymbol: string;
  children: DatapointGroup[];
  /** Datenpunkte direkt unter dieser Gruppe, bereits gefiltert. */
  rows: DatapointRow[];
  /** Treffer im ganzen Teilbaum. Steht als Zahl am Knoten, damit beim
   *  Stoebern sichtbar ist, wo etwas zu holen ist, bevor man aufklappt. */
  matchCount: number;
}

/** Eine Zeile des aufgeklappten Baums, fertig zum Virtualisieren. */
export type VisibleTreeRow =
  | { kind: 'group'; depth: number; group: DatapointGroup; expanded: boolean }
  | { kind: 'leaf'; depth: number; row: DatapointRow };

// ---------------------------------------------------------------------------
// Werttyp und Schreibbarkeit
// ---------------------------------------------------------------------------

/**
 * Ordnet den Server-Werttyp einer der vier Editor-Klassen zu.
 *
 * `Flags` bekommt bewusst KEINE Klasse: ein Flags-Punkt ist ein Bitfeld, kein
 * Bool und keine Auswahl aus einer Liste. Ein Schalter oder ein Aufklappmenue
 * darauf waere genau der Fehler, den der Typfilter verhindern soll. Wer
 * Flags-Punkte anbieten will, braucht ein eigenes Widget und dann auch eine
 * eigene Klasse hier.
 *
 * Die Aliasse (integer, number, double, real, text, boolean) sind Vorsicht,
 * keine Vermutung: der Endpoint liefert heute ausschliesslich die sechs
 * Enum-Namen, aber `pages/PlantSchemaPage.tsx:1604` prueft ebenfalls auf
 * 'Integer' und 'Number' mit, und ein importiertes Schema soll hier nicht
 * stumm durchfallen.
 */
export function classifyValueType(leaf: LeafDto): DatapointValueType | null {
  switch ((leaf.valueType ?? '').trim().toLowerCase()) {
    case 'bool':
    case 'boolean':
      return 'bool';
    case 'int':
    case 'integer':
    case 'number':
    case 'float':
    case 'double':
    case 'real':
    case 'decimal':
      return 'number';
    case 'enum':
      return 'enum';
    case 'string':
    case 'text':
      return 'string';
    default:
      return null;
  }
}

/** Passt der Punkt zur verlangten Werttyp-Klasse? */
export function matchesValueType(leaf: LeafDto, wanted: DatapointValueType): boolean {
  return classifyValueType(leaf) === wanted;
}

/**
 * Darf auf den Punkt geschrieben werden?
 *
 * Nur die Leaf-Richtung, zeichengleich zu `SendCommandModal.tsx:41`. Ob das
 * Geraet Kommandos annimmt (`DeviceDto.commandCapable`) steht bewusst NICHT
 * in dieser Pruefung: das Flag wird heute nirgends zum Sperren benutzt,
 * sondern nur als Abzeichen angezeigt (`PlantSchemaPage.tsx:1268`). Hier
 * mitzupruefen hiesse, Punkte zu verstecken, die der Kommandodialog sehr wohl
 * anbietet, und das waere die schlechtere Ueberraschung.
 */
export function isWritableLeaf(leaf: LeafDto): boolean {
  return leaf.writability === 'ReadWrite' || leaf.writability === 'WriteOnly';
}

/**
 * Baut aus einem {@link DatapointFilter} das Praedikat fuer den Baumaufbau.
 * Gibt `null` zurueck, wenn nichts einzuschraenken ist, damit der Aufrufer
 * den Filterdurchlauf ganz sparen kann.
 */
export function makeLeafPredicate(filter?: DatapointFilter): ((leaf: LeafDto) => boolean) | null {
  if (!filter || (filter.writable !== true && filter.valueType === undefined)) return null;
  const wantWritable = filter.writable === true;
  const wantType = filter.valueType;
  return (leaf) => {
    if (wantWritable && !isWritableLeaf(leaf)) return false;
    if (wantType !== undefined && !matchesValueType(leaf, wantType)) return false;
    return true;
  };
}

/** Deutsche Beschriftung der Werttyp-Klassen. */
export const VALUE_TYPE_LABELS: Record<DatapointValueType, string> = {
  number: 'Zahl',
  bool: 'Ja/Nein',
  enum: 'Auswahl',
  string: 'Text',
};

/** Kurzform des Filters fuer die Zeile ueber der Liste. Leer, wenn nichts
 *  eingeschraenkt ist. Der Autor soll sehen, warum ein Punkt fehlt, den er
 *  sucht, statt ihn fuer verschwunden zu halten. */
export function describeFilter(filter?: DatapointFilter): string {
  const parts: string[] = [];
  if (filter?.writable) parts.push('nur beschreibbare Punkte');
  if (filter?.valueType) parts.push(`Werttyp ${VALUE_TYPE_LABELS[filter.valueType]}`);
  return parts.join(' · ');
}

// ---------------------------------------------------------------------------
// Baum und flache Liste
// ---------------------------------------------------------------------------

/**
 * Laeuft das Anlagenschema ab und baut den gefilterten Stoeberbaum.
 *
 * `include` ist das Punkt-Praedikat (Werttyp, Schreibbarkeit, im Chart-Waehler
 * auch "ist ueberhaupt plotbar"). Gruppen, in deren Teilbaum kein Punkt
 * uebrigbleibt, fallen weg: ein Baum voller leerer Aeste ist beim Stoebern
 * schlimmer als ein kurzer Baum.
 *
 * Rekursion ohne Tiefenbremse, weil der Baum aus der Datenbank kommt und dort
 * ein Baum ist, kein Graph. Real sind es bis zu sechs Ebenen.
 */
export function buildDatapointGroups(
  schema: PlantSchemaResponse,
  include?: ((leaf: LeafDto) => boolean) | null,
): DatapointGroup[] {
  const groups: DatapointGroup[] = [];
  for (const device of schema.devices) {
    const group = buildDeviceGroup(device, include ?? null);
    if (group.matchCount > 0) groups.push(group);
  }
  return groups;
}

function buildDeviceGroup(device: DeviceDto, include: ((leaf: LeafDto) => boolean) | null): DatapointGroup {
  const children: DatapointGroup[] = [];
  for (const node of device.nodes) {
    const child = buildNodeGroup(device, node, '', include);
    if (child.matchCount > 0) children.push(child);
  }
  return {
    id: device.id,
    kind: 'device',
    label: device.name || device.symbol,
    path: '',
    deviceId: device.id,
    deviceSymbol: device.symbol,
    children,
    rows: [],
    matchCount: children.reduce((sum, c) => sum + c.matchCount, 0),
  };
}

function buildNodeGroup(
  device: DeviceDto,
  node: NodeDto,
  parentPath: string,
  include: ((leaf: LeafDto) => boolean) | null,
): DatapointGroup {
  const label = node.displayName ?? node.symbol;
  // Pfadform `a/b/c` zeichengleich zu der, die MultiLeafPicker seit jeher
  // baut: der Heuhaufen haengt daran, und beide Waehler sollen bei derselben
  // Eingabe dieselben Treffer zeigen.
  const path = parentPath ? `${parentPath}/${label}` : label;

  const rows: DatapointRow[] = [];
  for (const leaf of node.leaves) {
    if (include && !include(leaf)) continue;
    rows.push(makeRow(leaf, device, path));
  }

  const children: DatapointGroup[] = [];
  for (const child of node.children) {
    const built = buildNodeGroup(device, child, path, include);
    if (built.matchCount > 0) children.push(built);
  }

  return {
    id: node.id,
    kind: 'node',
    label,
    path,
    deviceId: device.id,
    deviceSymbol: device.symbol,
    children,
    rows,
    matchCount: rows.length + children.reduce((sum, c) => sum + c.matchCount, 0),
  };
}

function makeRow(leaf: LeafDto, device: DeviceDto, path: string): DatapointRow {
  const name = leaf.displayName || leaf.symbol;
  const haystack = `${leaf.symbol}\n${name}\n${leaf.unit ?? ''}\n${device.symbol}\n${path}`.toLowerCase();
  return {
    leaf,
    deviceId: device.id,
    deviceSymbol: device.symbol,
    deviceName: device.name || device.symbol,
    path,
    haystack,
  };
}

/** Sammelt alle Zeilen eines Baums in Baumreihenfolge ein. */
export function collectDatapointRows(groups: readonly DatapointGroup[]): DatapointRow[] {
  const out: DatapointRow[] = [];
  const walk = (group: DatapointGroup): void => {
    for (const row of group.rows) out.push(row);
    for (const child of group.children) walk(child);
  };
  for (const group of groups) walk(group);
  return out;
}

/**
 * Flache, alphabetisch stabile Liste aller passenden Punkte. Das ist die
 * Grundlage der Suche; die Sortierung nach Heuhaufen ist dieselbe wie bisher
 * im MultiLeafPicker, damit die leere Suche dort weiterhin dieselbe
 * Reihenfolge zeigt.
 */
export function flattenDatapointRows(
  schema: PlantSchemaResponse,
  include?: ((leaf: LeafDto) => boolean) | null,
): DatapointRow[] {
  return collectDatapointRows(buildDatapointGroups(schema, include))
    .sort((a, b) => a.haystack.localeCompare(b.haystack));
}

/** Nachschlagetabelle Leaf-Id auf Zeile. Der Weg vom gespeicherten
 *  Binding-Key zurueck zu Name und Pfad. */
export function indexRowsByLeafId(rows: readonly DatapointRow[]): Map<string, DatapointRow> {
  const map = new Map<string, DatapointRow>();
  // Leaf-Ids kommen vom Server als Guid im D-Format, der Binding-Key ist
  // kleingeschrieben (siehe `hmi/bindingKey.ts`). Klein als Schluessel, damit
  // das Nachschlagen nicht an der Schreibweise scheitert.
  for (const row of rows) map.set(row.leaf.id.toLowerCase(), row);
  return map;
}

// ---------------------------------------------------------------------------
// Suche
// ---------------------------------------------------------------------------

/**
 * Sortiert Treffer danach, wie eng die Eingabe die Zeile trifft: ein exaktes
 * Symbol steht ueber einem Praefix, ein Praefix ueber einem Vorkommen
 * irgendwo im Heuhaufen. Der optionale Geraetefilter schneidet vorher ab.
 * Leere Eingabe liefert die vorsortierte Liste.
 *
 * Wortgleich uebernommen aus MultiLeafPicker, damit dessen Verhalten sich
 * durch das Herausziehen nicht aendert.
 */
export function rankFilterDatapointRows(
  rows: readonly DatapointRow[],
  query: string,
  deviceId: string | null,
): DatapointRow[] {
  const scoped = deviceId ? rows.filter((r) => r.deviceId === deviceId) : rows;
  const q = query.trim().toLowerCase();
  if (!q) return scoped as DatapointRow[];
  interface Scored { row: DatapointRow; score: number }
  const scored: Scored[] = [];
  for (const row of scoped) {
    const symbol = row.leaf.symbol.toLowerCase();
    const name = (row.leaf.displayName ?? '').toLowerCase();
    let score = -1;
    if (symbol === q) score = 100;
    else if (name === q) score = 90;
    else if (symbol.startsWith(q)) score = 70;
    else if (name.startsWith(q)) score = 60;
    else if (row.haystack.includes(q)) score = 30;
    if (score >= 0) scored.push({ row, score });
  }
  scored.sort((a, b) => (b.score - a.score) || a.row.haystack.localeCompare(b.row.haystack));
  return scored.map((s) => s.row);
}

// ---------------------------------------------------------------------------
// Stoebern
// ---------------------------------------------------------------------------

/**
 * Welche Gruppen sind beim Oeffnen offen? Die Geraete, sonst nichts.
 *
 * Grund: an einem Regler haengen bis zu 4518 Punkte. Alles aufzuklappen
 * hiesse, dem Autor 4518 Zeilen vor die Suche zu stellen; alles zuzuklappen
 * hiesse, ihm bei der ueblichen Anlage mit einem Geraet einen einzigen Pfeil
 * hinzustellen. Geraete offen, Knoten zu, ist die Mitte.
 */
export function defaultExpandedGroupIds(groups: readonly DatapointGroup[]): string[] {
  return groups.filter((g) => g.kind === 'device').map((g) => g.id);
}

/**
 * Flacht den Baum auf die Zeilen ab, die gerade sichtbar sind. Nur diese
 * Liste geht in die Virtualisierung, deshalb ist die Lastfrage hier schon
 * halb beantwortet: ein zugeklappter Knoten kostet eine Zeile, nicht tausend.
 */
export function flattenVisibleTreeRows(
  groups: readonly DatapointGroup[],
  expanded: ReadonlySet<string>,
): VisibleTreeRow[] {
  const out: VisibleTreeRow[] = [];
  const walk = (group: DatapointGroup, depth: number): void => {
    const isOpen = expanded.has(group.id);
    out.push({ kind: 'group', depth, group, expanded: isOpen });
    if (!isOpen) return;
    for (const child of group.children) walk(child, depth + 1);
    // Untergruppen vor eigenen Punkten: sonst rutschen die Punkte eines
    // Knotens optisch unter seine Kinder und wirken wie deren Inhalt.
    for (const row of group.rows) out.push({ kind: 'leaf', depth: depth + 1, row });
  };
  for (const group of groups) walk(group, 0);
  return out;
}

/** Ids aller Gruppen, die auf dem Weg zu einem Punkt liegen. Damit laesst
 *  sich der Baum beim Oeffnen auf einen bereits gebundenen Punkt aufklappen. */
export function pathToLeaf(groups: readonly DatapointGroup[], leafId: string): string[] {
  const wanted = leafId.toLowerCase();
  const search = (group: DatapointGroup, trail: string[]): string[] | null => {
    const here = [...trail, group.id];
    if (group.rows.some((r) => r.leaf.id.toLowerCase() === wanted)) return here;
    for (const child of group.children) {
      const found = search(child, here);
      if (found) return found;
    }
    return null;
  };
  for (const group of groups) {
    const found = search(group, []);
    if (found) return found;
  }
  return [];
}
